import { components, GroupBase, MenuProps } from 'react-select';
import { Search } from 'lucide-react';
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerTitle,
} from '@/components/ui/Drawer.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import { cn } from '@/lib/utils.ts';

/**
 * Menu react-selecta narysowane jako arkusz: nagłówek, opcjonalne pole
 * wyszukiwania i `children`, czyli zwyczajna `MenuList` z opcjami — stąd
 * ten sam wygląd opcji co na desktopie i działające doczytywanie stron
 * w selectach asynchronicznych. Stan przychodzi z `useMobileSelectMenu`
 * przez `selectProps.mobileMenu`.
 */
const MobileSelectMenu = <
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
>(
  props: MenuProps<Option, IsMulti, Group>,
) => {
  const state = props.selectProps.mobileMenu;
  if (!state) return <components.Menu {...props} />;

  const { open, close, title, searchable, search, setSearch } = state;

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <DrawerContent
        aria-describedby={undefined}
        // Radix przenosi fokus do arkusza; z polem wyszukiwania to od razu
        // klawiatura zasłaniająca listę. Fraza wpisze się po dotknięciu pola.
        onOpenAutoFocus={(event) => event.preventDefault()}
        className="max-h-[85dvh] rounded-t-[22px] border-t-0 bg-basic-white"
      >
        <div className="flex flex-col gap-3 px-4 pb-2 pt-3">
          <DrawerTitle className="text-center text-base font-semibold text-base-text">
            {title ?? 'Wybierz'}
          </DrawerTitle>
          {searchable && (
            <div className="relative">
              <Search
                aria-hidden
                className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-neutral-400"
              />
              <input
                type="text"
                inputMode="search"
                enterKeyHint="search"
                autoComplete="off"
                aria-label="Szukaj opcji"
                placeholder="Szukaj..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="h-10 w-full rounded-md border border-neutral-200 bg-basic-white pl-9 pr-3 text-base text-base-text placeholder:text-neutral-400 focus:border-neutral-400 focus:outline-none"
              />
            </div>
          )}
        </div>
        <div
          className={cn(
            'flex min-h-0 flex-1 flex-col',
            // Ostatnia opcja nie może kleić się do krawędzi ekranu; pod
            // przyciskiem „Gotowe” odstęp daje już stopka.
            props.isMulti
              ? 'pb-1'
              : 'pb-[max(1.25rem,calc(env(safe-area-inset-bottom)+0.75rem))]',
          )}
        >
          {props.children}
        </div>
        {props.isMulti && (
          <DrawerFooter className="pt-2">
            <ActionButton tone="dark" size="block" onClick={close}>
              Gotowe
            </ActionButton>
          </DrawerFooter>
        )}
      </DrawerContent>
    </Drawer>
  );
};

export default MobileSelectMenu;
